import { APP_INITIALIZER, Injector, ModuleWithProviders, NgModule, Optional, SkipSelf } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NbAuthModule, NbPasswordAuthStrategy, NbAuthJWTToken, NbTokenService } from '@nebular/auth';
import { NbSecurityModule, NbRoleProvider } from '@nebular/security';
import { of as observableOf } from 'rxjs';

import { throwIfAlreadyLoaded } from './module-import-guard';
import { DataModule } from './data/data.module';
import { adoptHandoffCookie } from '../session-handoff';
import { environment } from '../../environments/environment';

const socialLinks = [];

// Adopt-side of the cross-origin session hand-off. The dashboard seeds its token
// in main.ts before bootstrap, but ng authenticates through Nebular, so the token
// must go into NbTokenService — a DI service only available once the app is up.
// An APP_INITIALIZER runs during bootstrap (before the router/guards), consuming
// the hand-off cookie and setting it as the Nebular JWT so the redirected customer
// lands already logged in (single strategy, so no owner name needed). No cookie → no-op.
//
// IMPORTANT: depend on Injector (not NbTokenService directly). Listing
// NbTokenService in `deps` forces Angular to construct it — and its NbAuth/HTTP
// dependency graph — while still assembling the APP_INITIALIZER multi-provider,
// which forms a circular provider dependency (RangeError: Maximum call stack
// size exceeded) that aborts bootstrap entirely. Resolving it lazily via
// injector.get() inside the factory runs after DI is fully built. No cycle.
export function handoffTokenInitializer(injector: Injector) {
	return () => {
		const jwt = adoptHandoffCookie(environment.sessionCookieDomain);
		if (jwt) {
			injector.get(NbTokenService).set(new NbAuthJWTToken(jwt)).subscribe();
		}
	};
}

export class NbSimpleRoleProvider extends NbRoleProvider {
	getRole() {
		// here you could provide any role based on any auth flow
		return observableOf('guest');
	}
}

export const NB_CORE_PROVIDERS = [
	...DataModule.forRoot().providers,
	...NbAuthModule.forRoot({

		strategies: [
			NbPasswordAuthStrategy.setup({
				name: 'default',
				baseEndpoint: environment.apiUrl,
				login: {
					endpoint: '',
				},
				requestPass: {
					endpoint: '',
					redirect: {
						success: null,
						failure: null,
					},
				},
				resetPass: {
					endpoint: '',
					redirect: {
						failure: null,
					},
				},
				logout: {
					endpoint: '',
				},
				token: {
					class: NbAuthJWTToken,
					key: 'token',
				},
			}),
		],
		forms: {
			login: {
				redirectDelay: 500,
				strategy: 'default',
				rememberMe: true,
				showMessages: {
					success: true,
					error: true,
				},
				socialLinks: socialLinks,
				defaultErrors: ['Login details are incorrect, please try again.'],
			},
			requestPassword: {
				strategy: 'default',
				showMessages: {
					success: true,
					error: true,
				},
			},
			resetPassword: {
				strategy: 'default',
				showMessages: {
					success: true,
					error: true,
				},
				defaultErrors: ['Expired password reset token.'],
			},
			logout: {
				strategy: 'default',
			},
		},
	}).providers,

	NbSecurityModule.forRoot({
		accessControl: {
			guest: {
				view: '',
			},
			user: {
				parent: 'guest',
				create: '*',
				edit: '*',
				remove: '*',
			},
		},
	}).providers,

	{
		provide: NbRoleProvider, useClass: NbSimpleRoleProvider,
	},

	{
		provide: APP_INITIALIZER,
		useFactory: handoffTokenInitializer,
		deps: [Injector],
		multi: true,
	},
];

@NgModule({
	imports: [
		CommonModule,
	],
	exports: [
		NbAuthModule,
	],
	declarations: [],
})
export class CoreModule {
	constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
		throwIfAlreadyLoaded(parentModule, 'CoreModule');
	}

	static forRoot(): ModuleWithProviders {
		return <ModuleWithProviders>{
			ngModule: CoreModule,
			providers: [
				...NB_CORE_PROVIDERS,
			],
		};
	}
}
